/**
 * WorkSuitGrid — the Pal Builder's "Work Suitability" card.
 *
 * Extracted from /app/frontend/src/pages/Builder.jsx to keep that page
 * under the team's 700-line guideline. Owns:
 *   • the section header with Reset / Max All buttons
 *   • the row of Workforce one-click presets (🏗 Builder, 🍳 Kitchen, …)
 *   • the 12 stepper rows (one per PalDefender work key)
 *
 * Pure-presentational — all state lives in Builder.jsx and is passed in
 * via props. This keeps the component testable in isolation and lets a
 * future Lite/Mobile builder reuse it without ceremony.
 */
import {
  Briefcase, Flame, Droplets, Sprout, Zap, Hammer, Apple, Trees,
  Pickaxe, Pill, Snowflake, Truck, Beef, Lock,
} from "lucide-react";
import {
  WORK_SUITS, WORK_PRESETS, MAX_WORK_LEVEL, presetWorkSuits,
} from "@/lib/builderPresets";

// Lucide icon lookup — the WORK_SUITS data table references icons by
// string name so /lib/builderPresets.js can stay React-free.
const WORK_ICONS = {
  Flame, Droplets, Sprout, Zap, Hammer, Apple, Trees, Pickaxe, Pill,
  Snowflake, Truck, Beef,
};

export function WorkSuitGrid({
  workSuits, setWorkSuit, setWorkSuits,
  naturalWorkSuits,
  onMaxWorkSuits, onResetWorkSuits,
}) {
  // PalDefender's `ExtraWorkSuitabilities` can only *boost* work types a
  // pal already possesses — non-native keys are silently dropped by the
  // game engine on first load. Use the pal's natural set as the source
  // of truth for "editable" rows; everything else is locked.
  const natural = naturalWorkSuits || {};
  const isNative = (key) => (natural[key] || 0) > 0;
  // Presets like "🏗 Builder" set 3 tags; only the natively-supported
  // ones actually take effect, so we filter the preset's tag list
  // against the pal's natives before applying.
  const applyPreset = (presetId) => {
    const full = presetWorkSuits(presetId);
    const filtered = Object.fromEntries(
      Object.entries(full).map(([k, v]) => [k, isNative(k) ? v : 0]),
    );
    setWorkSuits(filtered);
  };

  return (
    <div className="glass-card p-4" data-testid="builder-worksuit">
      <header className="flex items-center justify-between mb-3">
        <span className="label flex items-center gap-1.5">
          <Briefcase size={12} className="text-amber-300" /> Work Suitability
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={onResetWorkSuits}
            className="btn btn-ghost text-xs"
            data-testid="builder-reset-worksuits"
          >
            Reset
          </button>
          <button
            onClick={onMaxWorkSuits}
            className="btn btn-ghost text-xs"
            data-testid="builder-max-worksuits"
          >
            Max All
          </button>
        </div>
      </header>

      <p className="text-[10px] text-[var(--muted)] mb-3 leading-snug">
        Only work types the pal has naturally are editable — PalDefender
        can&apos;t add a work skill the species doesn&apos;t have. Bumps here
        are delivered as a <span className="font-mono">+bonus</span> on top
        of the pal&apos;s base level; PalDefender clamps each at{" "}
        <span className="font-mono">+{MAX_WORK_LEVEL}</span>.
      </p>

      {/* Workforce one-click presets — bias the pal toward a base-camp role. */}
      <div
        className="flex flex-wrap gap-1.5 mb-3"
        data-testid="builder-worksuit-presets"
      >
        {WORK_PRESETS.map((p) => {
          // Disable presets where the pal has none of the preset's tags
          // natively — clicking would set everything to 0 which is a
          // surprising rug-pull. Tooltip explains why.
          const applicable = p.tags.filter(isNative);
          const disabled = applicable.length === 0;
          return (
            <button
              key={p.id}
              type="button"
              disabled={disabled}
              onClick={() => applyPreset(p.id)}
              className={
                "text-[10px] px-2 py-1 rounded-md border border-white/10 transition-colors " +
                (disabled
                  ? "opacity-30 cursor-not-allowed"
                  : "hover:border-amber-400/40 hover:bg-amber-500/5")
              }
              data-testid={`builder-worksuit-preset-${p.id}`}
              title={
                disabled
                  ? `This pal has none of ${p.tags.join(", ")} natively`
                  : `Set ${applicable.join(", ")} to +${MAX_WORK_LEVEL}; everything else to 0`
              }
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {WORK_SUITS.map(({ key, label, iconName }) => (
          <WorkSuitRow
            key={key}
            workKey={key}
            label={label}
            Icon={WORK_ICONS[iconName] || Briefcase}
            value={workSuits[key] ?? 0}
            onChange={(v) => setWorkSuit(key, v)}
            disabled={!isNative(key)}
          />
        ))}
      </div>
    </div>
  );
}

/** One row in the work-suit grid: icon · label · −/dot-meter/+ stepper.
 *  When `disabled` (pal lacks this work natively) the entire row dims
 *  out and the +/− buttons are inert — clicking would have no in-game
 *  effect since PalDefender drops non-native ExtraWorkSuitabilities. */
function WorkSuitRow({ workKey, label, Icon, value, onChange, max, disabled = false }) {
  return (
    <div
      className={
        "flex items-center gap-2 bg-black/20 border border-white/5 rounded-md px-2 py-1.5 " +
        (disabled ? "opacity-40 grayscale" : "")
      }
      data-testid={`builder-worksuit-row-${workKey}`}
      title={disabled ? `${label}: not a natural work skill for this pal — locked.` : undefined}
    >
      <Icon size={13} className="text-amber-300 flex-shrink-0" />
      <span className="text-[11px] flex-1 truncate" title={label}>{label}</span>
      {disabled && <Lock size={10} className="text-[var(--muted)] flex-shrink-0" aria-label="locked" />}
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        className="text-[var(--muted)] hover:text-[var(--bone)] text-xs px-1.5 leading-none disabled:opacity-30"
        disabled={disabled || value <= 0}
        data-testid={`builder-worksuit-${workKey}-down`}
        aria-label={`Decrease ${label}`}
      >−</button>
      <div
        className="flex items-center gap-0.5"
        role="meter"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={MAX_WORK_LEVEL}
      >
        {Array.from({ length: MAX_WORK_LEVEL }).map((_, i) => (
          <span
            key={i}
            className={
              "w-1.5 h-1.5 rounded-full transition-colors " +
              (i < value ? "bg-amber-400" : "bg-white/10")
            }
          />
        ))}
      </div>
      <span
        className="text-[11px] font-mono w-4 text-right tabular-nums"
        data-testid={`builder-worksuit-${workKey}-value`}
      >{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(MAX_WORK_LEVEL, value + 1))}
        className="text-[var(--muted)] hover:text-[var(--bone)] text-xs px-1.5 leading-none disabled:opacity-30"
        disabled={disabled || value >= MAX_WORK_LEVEL}
        data-testid={`builder-worksuit-${workKey}-up`}
        aria-label={`Increase ${label}`}
      >+</button>
    </div>
  );
}
